import LoadSpinner from 'legacyfrontend/components/spinner';
import ErrorSnackbar from 'legacyfrontend/components/ErrorSnackbar';
import FlightCard from 'legacyfrontend/components/flights/FlightCard';
import { useFlightContext } from 'legacyfrontend/contexts/FlightDetailsContext';
import { useFlightsFilters } from 'legacyfrontend/contexts/FlightsFiltersContext';
import { useCallback, useState, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useGlobal } from 'legacyfrontend/contexts/GlobalContext';
import Flex from '../../../components/Flex';
import { useRefreshModal } from 'legacyfrontend/hooks/useRefreshModal';
import RefreshModal from 'legacyfrontend/components/home/components/RefreshModal';
import { useSelector } from 'react-redux';
import { useListingColors } from 'legacyfrontend/hooks/useListingColors';
import useFlightResultsFetch from 'legacyfrontend/flights/search/useFlightResultsFetch';

const FlightsListView = () => {
  const { t } = useTranslation('layout');
  const { onSearchCount, registerOnSearch } = useGlobal();
  const { setData, filteredData, isLoading, setIsLoading, error, setError, filters, handleFilters } =
    useFlightsFilters();
  const location = useLocation();
  const { setFlightInformation, handleCacheExpiration, setOneWayFlight, setIsLoading: setFlightContextLoading } = useFlightContext();
  const [errorMessage, setErrorMessage] = useState('');
  const [openToast, setOpenToast] = useState(false);
  const delegation = useSelector((state) => state.applicationState.delegation);
  const { showRefreshModal, setShowRefreshModal, handleRefreshConfirm } = useRefreshModal();
  const { background, text } = useListingColors();

  const showError = useCallback((message) => {
    setErrorMessage(message);
    setOpenToast(true);
  }, []);

  const handleCloseToast = useCallback(() => {
    setOpenToast(false);
  }, []);

  const { handleFetch, handleRetry, hasValidSearchParams } = useFlightResultsFetch({
    delegation,
    filters,
    handleCacheExpiration,
    handleFilters,
    isLoading,
    location,
    onSearchCount,
    registerOnSearch,
    setData,
    setError,
    setFlightContextLoading,
    setFlightInformation,
    setIsLoading,
    setOneWayFlight,
    showError,
  });

  const handleRefresh = useCallback(() => {
    handleFetch();
  }, [handleFetch]);

  if (onSearchCount === 0 && !hasValidSearchParams) {
    return (
      <div className='container flight__list-view__area flight__list-view__area--empty'>
        <h2>Busca vuelos disponibles</h2>
        <p style={{ textAlign: 'center', padding: '0 2rem' }}>
          Completa el formulario de búsqueda para encontrar los mejores vuelos
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div
        className='container flight__list-view__area loading-flights'
        style={{ textAlign: 'center', padding: '4rem 2rem' }}
      >
        <LoadSpinner />
        <h2 style={{ marginTop: '2rem' }}>Buscando los mejores vuelos para ti</h2>
        <p style={{ marginTop: '1rem', color: '#666' }}>Esto puede tomar unos segundos...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className='container flight__list-view__area flight__list-view__area--error'>
        <h2>¡Ups! Algo salió mal</h2>
        <p style={{ textAlign: 'center', padding: '0 2rem' }}>
          No pudimos cargar los vuelos. Por favor, intenta nuevamente en unos momentos o ajuste los
          filtros de búsqueda.
        </p>
        <button
          onClick={handleRetry}
          style={{
            backgroundColor: background,
            color: text,
            border: `2px solid ${background}`,
            padding: '12px 24px',
            borderRadius: '8px',
            fontSize: '16px',
            fontWeight: '500',
            cursor: 'pointer',
            marginTop: '20px',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = 'transparent';
            e.target.style.color = background;
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = background;
            e.target.style.color = text;
          }}
        >
          🔄 Reintentar búsqueda
        </button>
        <ErrorSnackbar message={errorMessage} onClose={handleCloseToast} open={openToast} />
      </div>
    );
  }

  if (!hasValidSearchParams) {
    return (
      <div className='container flight__list-view__area flight__list-view__area--empty'>
        <h2>No encontramos vuelos disponibles</h2>
        <p style={{ textAlign: 'center', padding: '0 2rem' }}>
          Por favor, completa todos los campos de búsqueda para ver los vuelos disponibles.
        </p>
      </div>
    );
  }

  if (!filteredData || filteredData.length === 0) {
    return (
      <div className='container flight__list-view__area flight__list-view__area--empty'>
        <h2>No encontramos vuelos disponibles</h2>
        <p style={{ textAlign: 'center', padding: '0 2rem' }}>
          Prueba ajustando los filtros de búsqueda o seleccionando fechas diferentes.
        </p>
      </div>
    );
  }

  return (
    <div className='flight__list-view__area'>
      <Flex className='flight__list__view__wrapper--flight'>
        <Flex className='flight__list__view__wrapper--flight__results' vertical>
          {filteredData.map((flightData, index) => {
            if (!flightData || !flightData.flight || flightData.flight.length === 0) {
              return null;
            }
            return <FlightCard {...flightData} key={`${flightData.currency}-${index}`} />;
          })}
        </Flex>
      </Flex>
      <RefreshModal
        disableBackdropClick
        disableEscapeKeyDown
        onClose={() => setShowRefreshModal(false)}
        onConfirm={() => handleRefreshConfirm(handleRefresh)}
        open={showRefreshModal}
      />
      <ErrorSnackbar message={errorMessage} onClose={handleCloseToast} open={openToast} />
    </div>
  );
};

export default memo(FlightsListView);
